import type { ReactNode } from 'react'
import clsx from 'clsx'
import { motion } from 'framer-motion'

interface Props {
  label: ReactNode
  value: ReactNode
  
  caption?: ReactNode
  
  footer?: ReactNode
  
  icon?: ReactNode
  
  tone?: 'brand' | 'good' | 'bad' | 'warn' | 'neutral'
  
  emphasis?: boolean
  className?: string
}

const TONE_CLASS: Record<NonNullable<Props['tone']>, string> = {
  brand: 'before:bg-brand-primary/40 ring-brand-primary/25',
  good: 'before:bg-[color:var(--signal-good)]/40 ring-[color:var(--signal-good)]/25',
  bad: 'before:bg-[color:var(--signal-bad)]/40 ring-[color:var(--signal-bad)]/25',
  warn: 'before:bg-[color:var(--signal-warn)]/40 ring-[color:var(--signal-warn)]/25',
  neutral: 'before:bg-brand-accent/40 ring-brand-accent/25',
}

export function KpiCard({
  label,
  value,
  caption,
  footer,
  icon,
  tone = 'brand',
  emphasis = false,
  className,
}: Props) {
  return (
    <motion.div
      layout
      initial={false}
      className={clsx(
        'relative isolate flex h-full flex-col gap-3 overflow-hidden rounded-3xl border border-border bg-surface-1/80 p-5 transition',
        'before:pointer-events-none before:absolute before:-inset-px before:-z-10 before:rounded-3xl before:blur-2xl before:opacity-30 before:transition',
        TONE_CLASS[tone],
        emphasis && 'ring-1',
        className,
      )}
    >
      <div className="flex min-w-0 items-start justify-between gap-2">
        <h3 className="min-w-0 truncate text-xs uppercase tracking-[0.18em] text-ink-subtle">
          {label}
        </h3>
        {icon && <span className="shrink-0 text-ink-muted">{icon}</span>}
      </div>
      <div className="flex-1">
        <div className="font-display text-kpi-lg leading-none text-ink">{value}</div>
        {caption && <div className="mt-2 text-sm text-ink-muted">{caption}</div>}
      </div>
      {footer && <div className="mt-auto flex items-center justify-between gap-2">{footer}</div>}
    </motion.div>
  )
}
